import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "bella_cart_v1";

function lineTotal(line) {
  const addonsSum = line.selected_addons.reduce((s, a) => s + (Number(a.price) || 0), 0);
  return (Number(line.base_price) + addonsSum) * line.quantity;
}

function lineCalories(line) {
  return Number(line.calories || 0) * line.quantity;
}

export function CartProvider({ children }) {
  const [lines, setLines] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (_e) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);

  function addLine(line) {
    setLines((prev) => [...prev, { ...line, line_total: lineTotal(line) }]);
  }

  function updateQuantity(index, qty) {
    setLines((prev) =>
      prev
        .map((l, i) =>
          i === index ? { ...l, quantity: qty, line_total: lineTotal({ ...l, quantity: qty }) } : l,
        )
        .filter((l) => l.quantity > 0),
    );
  }

  function removeLine(index) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }

  function clear() {
    setLines([]);
  }

  const { totalItems, totalAmount, totalCalories } = useMemo(() => {
    return {
      totalItems: lines.reduce((s, l) => s + l.quantity, 0),
      totalAmount: lines.reduce((s, l) => s + l.line_total, 0),
      totalCalories: lines.reduce((s, l) => s + lineCalories(l), 0),
    };
  }, [lines]);

  return (
    <CartContext.Provider
      value={{ lines, addLine, updateQuantity, removeLine, clear, totalItems, totalAmount, totalCalories }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
